import { useCallback, useMemo, useState } from 'react';
import { QuestionTypeEnum } from '@/models/Question';
import {
  Identifier,
  QuestionViewPropsFor,
} from '@/components/question/view/types';
import {
  BANK_ID,
  DnDToken,
  Placement,
  SlotConfig,
} from '@/utils/questions/dnd/types';
import {
  FillInTheBlankSegment,
  parseFillInTheBlank,
} from '@/utils/questions/parseFillInTheBlank';

export const blankSlotId = (index: number) => `blank-${index}`;

const isBlank = (
  segment: FillInTheBlankSegment,
): segment is Extract<FillInTheBlankSegment, { type: 'blank' }> =>
  segment.type === 'blank';

export function useFillInTheBlankAnswerView(
  props: QuestionViewPropsFor<QuestionTypeEnum.FILL_IN_THE_BLANK>,
) {
  const { question, value, mode } = props;
  const onChange = props.mode === 'answering' ? props.onChange : undefined;

  const segments = useMemo(
    () => parseFillInTheBlank(question.question),
    [question.question],
  );

  const blanks = useMemo(() => segments.filter(isBlank), [segments]);

  const tokens = useMemo<DnDToken[]>(
    () => question.options.map(option => ({ id: option.id, content: option })),
    [question.options],
  );

  const slots = useMemo<SlotConfig[]>(
    () => blanks.map(blank => ({ id: blankSlotId(blank.index), capacity: 1 })),
    [blanks],
  );

  const [bankOrder, setBankOrder] = useState<Identifier[]>([]);

  const placement = useMemo<Placement>(
    () => ({
      ...Object.fromEntries(
        blanks.map(blank => {
          const optionId = value?.[blank.index];
          return [blankSlotId(blank.index), optionId != null ? [optionId] : []];
        }),
      ),
      [BANK_ID]: bankOrder,
    }),
    [blanks, value, bankOrder],
  );

  const disabled = mode !== 'answering';

  const handlePlacementChange = useCallback(
    (next: Placement) => {
      setBankOrder(next[BANK_ID] ?? []);
      const fills = blanks.map(
        blank => next[blankSlotId(blank.index)]?.at(0) ?? null,
      );
      onChange?.(fills.some(id => id != null) ? fills : null);
    },
    [blanks, onChange],
  );

  return {
    segments,
    tokens,
    slots,
    placement,
    handlePlacementChange,
    disabled,
  };
}
